import PalaceCell from "@/components/PalaceCell";

// Sky Horse Icon Component
function SkyHorseIcon() {
  return (
    <div className="sky-horse-icon" title="Sky Horse (驿马) - Travel Star">
      <img src="/sky-horse.png" alt="Sky Horse" />
    </div>
  );
}

// Corner Label Component - positioned at grid corners
function CornerLabel({ direction, element, numbers, position }) {
  const isBottom = position.includes('bottom');
  
  if (isBottom) {
    return (
      <div className={`corner-label corner-${position}`}>
        <div className="label-content">
          <span className="corner-element">{element}</span>
          <span className="corner-numbers">{numbers}</span>
          <span className="corner-dir">{direction}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`corner-label corner-${position}`}>
      <div className="label-content">
        <span className="corner-dir">{direction}</span>
        <span className="corner-element">{element}</span>
        <span className="corner-numbers">{numbers}</span>
      </div>
    </div>
  );
}

// Edge Label Component - positioned at grid edges (mid-points)
function EdgeLabel({ direction, element, numbers, position }) {
  const isBottom = position === 'bottom';
  
  if (isBottom) {
    return (
      <div className={`edge-label edge-${position}`}>
        <div className="label-content">
          <span className="edge-element">{element}</span>
          <span className="edge-numbers">{numbers}</span>
          <span className="edge-dir">{direction}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`edge-label edge-${position}`}>
      <div className="label-content">
        <span className="edge-dir">{direction}</span>
        <span className="edge-element">{element}</span>
        <span className="edge-numbers">{numbers}</span>
      </div>
    </div>
  );
}

// Sky Horse marker positions based on palace number
// Palace 4 (SE) -> top-left area
// Palace 9 (S) -> top-center area  
// Palace 2 (SW) -> top-right area
// Palace 3 (E) -> left-top area
// Palace 7 (W) -> right-top area
// Palace 8 (NE) -> bottom-left area
// Palace 1 (N) -> bottom-center area
// Palace 6 (NW) -> bottom-right area
function SkyHorseMarker({ palace, position }) {
  if (!palace) return null;
  return (
    <div className={`sky-horse-marker sky-horse-${position}`}>
      <SkyHorseIcon />
    </div>
  );
}

// DE Marker Component - positioned at upper-left corner outside each palace
function DeMarker({ position }) {
  return (
    <div className={`de-marker-outer de-${position}`}>
      <span className="de-badge">DE 空</span>
    </div>
  );
}

export default function QimenChart({ palaces, gridLayout, metadata }) {
  if (!palaces || !gridLayout) return null;

  // Get markers from metadata (all from lookup - mirrored from qimenplotter)
  const skyHorsePalace = metadata?.skyhorse_palace;
  
  // DE (Void/空亡) palaces
  const dePalaces = metadata?.de_palaces || [];

  // Map palace number to position for DE markers
  const getDePosition = (palace) => {
    const positionMap = {
      4: 'se',      // SE - top-left palace
      9: 's',       // S - top-center palace
      2: 'sw',      // SW - top-right palace
      3: 'e',       // E - middle-left palace
      7: 'w',       // W - middle-right palace
      8: 'ne',      // NE - bottom-left palace
      1: 'n',       // N - bottom-center palace
      6: 'nw',      // NW - bottom-right palace
    };
    return positionMap[palace];
  };

  // Map palace number to position
  const getSkyHorsePosition = (palace) => {
    const positionMap = {
      4: 'top-left',      // SE
      9: 'top-center',    // S
      2: 'top-right',     // SW
      3: 'left',          // E
      7: 'right',         // W
      8: 'bottom-left',   // NE
      1: 'bottom-center', // N
      6: 'bottom-right',  // NW
    };
    return positionMap[palace];
  };

  const skyHorsePosition = getSkyHorsePosition(skyHorsePalace);

  return (
    <div className="qimen-chart-wrapper" data-testid="qimen-chart">
      {/* Chart container with grid and overlaid labels */}
      <div className="chart-container">
        {/* Main 3x3 Grid */}
        <div className="qimen-grid" data-testid="qimen-grid">
          {gridLayout.flat().map((palaceNum) => {
            const palace = palaces[String(palaceNum)];
            if (!palace) return null;
            return (
              <PalaceCell
                key={palaceNum}
                palace={palace}
                hasDE={false}
              />
            );
          })}
        </div>

        {/* DE Markers - positioned outside palaces at upper-left corners */}
        {dePalaces.map((palaceNum) => {
          const position = getDePosition(palaceNum);
          if (!position) return null;
          return <DeMarker key={`de-${palaceNum}`} position={position} />;
        })}

        {/* Corner Labels - positioned at actual grid corners */}
        <CornerLabel direction="SE" element="WOOD" numbers="2-4" position="top-left" />
        <CornerLabel direction="SW" element="EARTH" numbers="5-2" position="top-right" />
        <CornerLabel direction="NE" element="EARTH" numbers="4-8" position="bottom-left" />
        <CornerLabel direction="NW" element="METAL" numbers="7-6" position="bottom-right" />

        {/* Edge Labels - positioned at mid-points of grid edges */}
        <EdgeLabel direction="S" element="FIRE" numbers="1-9" position="top" />
        <EdgeLabel direction="N" element="WATER" numbers="8-1" position="bottom" />
        <EdgeLabel direction="E" element="WOOD" numbers="3-3" position="left" />
        <EdgeLabel direction="W" element="METAL" numbers="6-7" position="right" />

        {/* Sky Horse Marker - positioned in dedicated area */}
        {skyHorsePosition && (
          <SkyHorseMarker palace={skyHorsePalace} position={skyHorsePosition} />
        )}
      </div>
    </div>
  );
}
